import * as React from "react";
import StarRating from "./StarRating";
import ConfigContext from "../context/ConfigContext";
import { EngagementConfiguration } from "../types";
import Header from "./Header";
import DefaultHeaderLogo from "./DefaultHeaderLogo";
import isMobile from "../helpers/isMobile";

interface ConfigExtension {
  config: EngagementConfiguration;
}

interface ChatHeaderProps {
  agentName: string;
  agentHeadshotUrl: string;
  agentRating: number;
  isPopout: boolean;
  onSetAgentRating: (rating: number) => void;
  onPopout: () => void;
  onCollapse: () => void;
  onClose: () => void;
  onClickEmailButton: () => void;
  onClickPrintButton: () => void;
}

/**
 * Any changes to this component or its children should be accompanied by
 * a `yarn build` and testing the Engagement Design screen in agent console.
 * Keep this component as dumb as possible so it can be easily reused.
 */

class ChatHeader extends React.Component<
  ChatHeaderProps & ConfigExtension,
  {}
> {
  renderRatingContainer() {
    if (!this.props.config.ChatRatingEnabled) {
      return null;
    }

    return (
      <div className="rating-container">
        <span className="rating-text velaro-text-primary">
          {this.props.config.ChatRatingText}
        </span>
        <StarRating
          questionId={0}
          value={this.props.agentRating}
          onChange={(rating: number) => this.props.onSetAgentRating(rating)}
        />
      </div>
    );
  }

  renderAgentHeadshot() {
    return (
      <div className="velaro-header-headshot">
        <img
          src={this.props.agentHeadshotUrl}
          alt={`${this.props.agentName} profile`}
        />
      </div>
    );
  }

  renderHeaderImage() {
    switch (this.props.config.ChatHeaderImageType) {
      case "AgentHeadshot":
        return this.renderAgentHeadshot();
      case "CompanyLogo":
        return <DefaultHeaderLogo />;
      case "None":
        return null;
      default:
        throw new Error("unhandled ChatHeaderImageType");
    }
  }

  useMinimizedHeader() {
    if (isMobile()) {
      return this.props.config.UseMinimizedHeaderMobile;
    }
    return this.props.config.UseMinimizedHeaderDesktop;
  }

  render() {
    const ratingContainer = this.renderRatingContainer();

    return (
      <Header
        isPopout={this.props.isPopout}
        onCollapse={this.props.onCollapse}
        onEmail={this.props.onClickEmailButton}
        onClose={this.props.onClose}
        onPrint={this.props.onClickPrintButton}
        onPopout={this.props.onPopout}
        headerContent={
          <React.Fragment>
            {this.renderHeaderImage()}
            <div className="velaro-header-agent-name velaro-text-primary">
              {this.props.agentName}
            </div>
            {ratingContainer}
          </React.Fragment>
        }
        menuFooterContent={
          ratingContainer && (
            <div className={this.useMinimizedHeader() ? "" : "velaro-mobile"}>
              <div className="velaro-header-menu-rate">{ratingContainer}</div>
            </div>
          )
        }
      />
    );
  }
}

export default (props: ChatHeaderProps) => (
  <ConfigContext.Consumer>
    {(config) => <ChatHeader config={config} {...props} />}
  </ConfigContext.Consumer>
);
